import { Injectable } from '@angular/core';

import camelCaseKeys from 'camelcase-keys';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import {
  IAPIFetchResponse,
  IAPIFetchRequestOptions,
  IAPIServiceRequestOptions
} from '@sondermind/utilities/models-http';

import { IUserProfileImage } from './models/user-profile-image.interface';

@Injectable()
export class UserProfileHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService
  ) { }

  private xCoord: number | undefined;
  private yCoord: number | undefined;
  private hCoord: number | undefined;
  private wCoord: number | undefined;

  updateCoords(h: number, w: number, x: number, y: number): void {
    this.hCoord = h;
    this.wCoord = w;
    this.xCoord = x;
    this.yCoord = y;
  }

  // GET: /user_profiles/{user_id}/photo
  getImage(
    userId: number,
    options: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IAPIFetchResponse<IUserProfileImage> | null> {
    const url = `${this.configurationService.env.svcBase}/core/user_profiles/${userId}/photo`;
    const params = this.http.generateFetchParams(options);

    return this.http
      .get<IAPIFetchResponse<IUserProfileImage>>(url, params)
      .pipe(
        map((r) => ({ ...r, data: this.convertToApplicationFormat(r.data) })),
        this.http.handleHttpError(options.error)
      );
  }

  // POST: /user_profiles/{user_id}/photo
  newPhoto(
    userId: number,
    options?: Partial<IAPIServiceRequestOptions>
  ): Observable<IAPIFetchResponse<IUserProfileImage> | null> {
    const url = `${this.configurationService.env.svcBase}/core/user_profiles/${userId}/photo`;
    const params = this.http.generateServiceParams(options ?? {});

    return this.http
      .post<IAPIFetchResponse<IUserProfileImage>>(url, { userId }, params)
      .pipe(
        map((r) => ({ ...r, data: this.convertToApplicationFormat(r.data) })),
        this.http.handleHttpError(options?.error ?? {})
      );
  }

  // PUT: /user_profiles/{user_id}/photo
  updatePhoto(
    userId: number,
    imageName: string,
    image: string,
    cropH: number | undefined = this.hCoord,
    cropW: number | undefined = this.wCoord,
    cropX: number | undefined = this.xCoord,
    cropY: number | undefined = this.yCoord,
    options: Partial<IAPIServiceRequestOptions> = {}
  ): Observable<IAPIFetchResponse<IUserProfileImage> | null> {
    const url = `${this.configurationService.env.svcBase}/core/user_profiles/${userId}/photo`;
    const params = this.http.generateServiceParams(options);

    return this.http.put<IAPIFetchResponse<IUserProfileImage>>(
      url,
      {
        image_name: imageName,
        image,
        crop_h: cropH,
        crop_w: cropW,
        crop_x: cropX,
        crop_y: cropY
      },
      params
    ).pipe(
      map((r) => ({ ...r, data: this.convertToApplicationFormat(r.data) })),
      this.http.handleHttpError(options.error)
    );
  }

  /**
     * Gets and returns the Blob version of an image at the provided URL.
     * Due to how we set options in the `HttpService` sub class, this method
     * uses the instance of `HttpClient` that is a public member of `HttpService`.
     *
     * @param {string} url
     * @returns {Observable<Blob>}
     * @memberof UserProfileHttpService
     */
  getBlobImage(url: string): Observable<Blob | null> {
    return this.http.http.get(url, { responseType: 'blob' })
      .pipe(
        this.http.handleHttpError()
      );
  }

  private convertToApplicationFormat(snakeCasedObject: object): IUserProfileImage {
    return camelCaseKeys(snakeCasedObject) as IUserProfileImage;
  }
}
